/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <ListOfArticles {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section } from "@bw/bits"
import { ListFilters } from "@bw/partials"
import { ListOfArticles as ListOfArticlesModule } from "@bw/modules"
import useCollectionArticles from "hooks/useCollectionArticles"

const ListOfArticles = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  backgroundVariant,
  isFollowedByModuleWithBackgroundVariant,
  category,
  articleType,
  orderedBy,
  maxItems,
  numberOfColumns,
  displayFilters,
  location,
}) => {
  const articles = useCollectionArticles({
    category: category?.slug,
    type: articleType?.slug,
    limit: maxItems,
    order: orderedBy?.value,
  })

  if (articles.length === 0) {
    return null
  }

  const columns = numberOfColumns.value
    ? parseInt(numberOfColumns.value)
    : undefined

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        idAttribute,
        classAttribute,
        backgroundVariant,
        isFollowedByModuleWithBackgroundVariant,
      }}
    >
      {displayFilters ? (
        <ListFilters items={articles}>
          {({ items }) => (
            <ListOfArticlesModule
              numberOfColumns={columns}
              articles={items}
              location={location}
            />
          )}
        </ListFilters>
      ) : (
        <ListOfArticlesModule
          numberOfColumns={columns}
          articles={articles}
          location={location}
        />
      )}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on ListOfArticlesModule {
 *   ...ListOfArticlesModuleFragment
 * }
 *
 * This is also what you will get in the props of your ListOfArticles
 * resolver component.
 */
export const query = graphql`
  fragment ListOfArticlesModuleFragment on ListOfArticlesModule {
    displayFilters
    numberOfColumns {
      value
    }
    maxItems
    orderedBy {
      value
    }
    category {
      slug
    }
    articleType {
      slug
    }
  }
`

export { ListOfArticles }
